import { useRef, useState } from 'react';
import moment from 'moment';
import { Switch } from '@mui/material';
import { CustomRatioImage } from '@/components/image/CustomRatioImage';

const COUPANG_PARTNERS_DISCLOSURE = '이 포스팅은 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다.';
const DISCLOSURE_MAX_LENGTH = 200;

// ms ↔ <input type="datetime-local"> 값 (브라우저 로컬 시간 기준)
const toInputValue = (ms) => ms ? moment(ms).format('YYYY-MM-DDTHH:mm') : '';
const toMs = (value) => value ? new Date(value).getTime() : null;
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const formatDateTime = (ms) => `${moment(ms).format('YYYY.MM.DD')} (${WEEKDAYS[moment(ms).day()]}) ${moment(ms).format('HH:mm')}`;

// 켤 때 기본값: 시작 = 다음 정각, 종료 = 시작(없으면 다음 정각) + 7일
const getNextHour = () => moment().add(1, 'hour').startOf('hour').valueOf();
const getDefaultUntil = (visibleFrom) => moment(visibleFrom || getNextHour()).add(7, 'days').valueOf();

const isOn = (value) => value !== null && value !== undefined;

export function validateLinkExtraFields(form) {
    if (isOn(form?.disclosure) && !form.disclosure.trim()) {
        return '고지 문구를 입력해 주세요. 사용하지 않으면 고지 문구를 꺼주세요.';
    }

    if (form?.disclosure?.length > DISCLOSURE_MAX_LENGTH) {
        return `고지 문구는 ${DISCLOSURE_MAX_LENGTH}자 이내로 입력 가능합니다.`;
    }

    if (isOn(form?.visibleFrom) && isOn(form?.visibleUntil) && form.visibleUntil <= form.visibleFrom) {
        return '공개 종료 시각은 공개 시작 시각보다 뒤여야 합니다.';
    }

    return null;
}

// 현재 설정 기준 공개 상태 요약
function getScheduleSummary({ visibleFrom, visibleUntil }) {
    const now = Date.now();

    if (isOn(visibleUntil) && visibleUntil <= now) {
        return { text: '공개 종료 시각이 지나 공개 페이지에 보이지 않습니다.', className: 'text-gray-500' };
    }

    if (isOn(visibleFrom) && now < visibleFrom) {
        return {
            text: `${formatDateTime(visibleFrom)}부터 공개${isOn(visibleUntil) ? `, ${formatDateTime(visibleUntil)}에 종료` : ''}됩니다.`,
            className: 'text-blue-600',
        };
    }

    return {
        text: isOn(visibleUntil) ? `지금 공개 중이며, ${formatDateTime(visibleUntil)}에 종료됩니다.` : '지금 바로 공개됩니다.',
        className: 'text-green-700',
    };
}

function ToggleRow({ title, description, checked, onToggle, children }) {
    return (
        <div className="flex flex-col gap-2 p-3">
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-semibold">{title}</div>
                    <div className="text-xs text-gray-500">{description}</div>
                </div>
                <Switch size='small' color='success' checked={checked} onChange={(e) => onToggle(e.target.checked)} />
            </div>
            {checked && children}
        </div>
    );
}

/**
 * 링크 모듈의 고급 옵션: 고지 문구 / 공개 예약 / 앱으로 열기 (추가·수정 모달 공용)
 * 값이 null 이면 꺼진 상태. onChange 에는 바뀐 필드만 담은 객체를 넘긴다.
 */
export function WegetLinkExtraFields({ form, onChange }) {
    // 껐다 다시 켤 때 직전에 입력한 값을 되살리기 위한 보관소
    const lastValuesRef = useRef({});

    const handleToggle = (field, checked, getDefaultValue) => {
        if (checked) {
            onChange({ [field]: lastValuesRef.current[field] ?? getDefaultValue() });
            return;
        }

        lastValuesRef.current[field] = form?.[field];
        onChange({ [field]: null });
    }

    const disclosureOn = isOn(form?.disclosure);
    const visibleFromOn = isOn(form?.visibleFrom);
    const visibleUntilOn = isOn(form?.visibleUntil);
    const isInvalidSchedule = visibleFromOn && visibleUntilOn && form.visibleUntil <= form.visibleFrom;
    const scheduleSummary = getScheduleSummary(form || {});
    const activeCount = [disclosureOn, visibleFromOn, visibleUntilOn].filter(Boolean).length;
    // 필드가 없으면(기존 링크) 켜진 것으로 본다
    const openInApp = form?.openInApp !== false;

    // 이미 켜진 옵션이 있거나 앱 열기를 끈 링크면 펼친 상태로 시작
    const [open, setOpen] = useState(activeCount > 0 || !openInApp);

    return (
        <div className="flex flex-col rounded-lg border border-gray-200">
            <button
                type="button"
                className="flex items-center justify-between p-3 text-left"
                onClick={() => setOpen(!open)}
            >
                <span className="text-sm font-semibold">
                    고급 옵션
                    {activeCount > 0 && <span className="ml-2 text-xs font-medium text-green-700">{activeCount}개 사용 중</span>}
                </span>
                <span className="w-5">
                    <CustomRatioImage width={1} height={1}>
                        <CustomRatioImage.Image src={open ? '/assets/icons/expandLess_000000.svg' : '/assets/icons/expandMore_000000.svg'} />
                    </CustomRatioImage>
                </span>
            </button>
            {open &&
                <div className="flex flex-col divide-y divide-gray-200 border-t border-gray-200">
                    <ToggleRow
                        title="고지 문구"
                        description="링크 아래에 항상 표시되는 안내 문구입니다. (예: 쿠팡 파트너스)"
                        checked={disclosureOn}
                        onToggle={(checked) => handleToggle('disclosure', checked, () => '')}
                    >
                        <textarea
                            rows={3}
                            maxLength={DISCLOSURE_MAX_LENGTH}
                            value={form?.disclosure || ''}
                            onChange={(e) => onChange({ disclosure: e.target.value })}
                            placeholder="표시할 문구를 입력해 주세요."
                            className="resize-none rounded border border-gray-300 p-2 text-sm"
                        />
                        <div className="flex items-center justify-between">
                            <button
                                type="button"
                                className="rounded-full border border-gray-300 px-2.5 py-1 text-xs text-gray-700"
                                onClick={() => onChange({ disclosure: COUPANG_PARTNERS_DISCLOSURE })}
                            >
                                + 쿠팡 파트너스 문구
                            </button>
                            <span className="text-xs text-gray-400">{form?.disclosure?.length || 0}/{DISCLOSURE_MAX_LENGTH}</span>
                        </div>
                    </ToggleRow>

                    <ToggleRow
                        title="공개 시작 예약"
                        description="지정한 시각부터 공개 페이지에 보입니다."
                        checked={visibleFromOn}
                        onToggle={(checked) => handleToggle('visibleFrom', checked, getNextHour)}
                    >
                        <input
                            type="datetime-local"
                            value={toInputValue(form?.visibleFrom)}
                            onChange={(e) => e.target.value && onChange({ visibleFrom: toMs(e.target.value) })}
                            className="rounded border border-gray-300 p-2 text-sm"
                        />
                    </ToggleRow>

                    <ToggleRow
                        title="공개 종료 예약"
                        description="지정한 시각에 공개 페이지에서 자동으로 숨겨집니다."
                        checked={visibleUntilOn}
                        onToggle={(checked) => handleToggle('visibleUntil', checked, () => getDefaultUntil(form?.visibleFrom))}
                    >
                        <input
                            type="datetime-local"
                            value={toInputValue(form?.visibleUntil)}
                            onChange={(e) => e.target.value && onChange({ visibleUntil: toMs(e.target.value) })}
                            className={`rounded border p-2 text-sm ${isInvalidSchedule ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        {isInvalidSchedule &&
                            <span className="text-xs text-red-500">공개 종료 시각은 공개 시작 시각보다 뒤여야 합니다.</span>
                        }
                    </ToggleRow>

                    <ToggleRow
                        title="인앱 브라우저에서 앱으로 열기"
                        description="인스타·카톡에서 누르면 스마트스토어·블로그는 네이버 앱, 유튜브는 유튜브 앱으로 엽니다."
                        checked={openInApp}
                        onToggle={(checked) => onChange({ openInApp: checked })}
                    />

                    {(visibleFromOn || visibleUntilOn) && !isInvalidSchedule &&
                        <div className={`px-3 py-2 text-xs ${scheduleSummary.className}`}>
                            {scheduleSummary.text}
                        </div>
                    }
                </div>
            }
        </div>
    );
}
